import { useState, useEffect, useRef } from 'react'
import { ShieldCheck, Loader2, XCircle, CheckCircle, ArrowLeft } from 'lucide-react'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { ProviderMode } from '@/pages/ProviderMode'

interface ProviderAuthGateProps {
  onBack: () => void
}

type GateState =
  | { phase: 'checking' }
  | { phase: 'setup-qr';    secret: string; qrBase64: string }
  | { phase: 'setup-confirm'; secret: string; qrBase64: string; confirming: boolean; error: string | null }
  | { phase: 'verify';      verifying: boolean; error: string | null }
  | { phase: 'unlocked' }

export function ProviderAuthGate({ onBack }: ProviderAuthGateProps) {
  const { proxyUrl, proxyToken } = useProxyConfig()
  const [state, setState] = useState<GateState>({ phase: 'checking' })
  const [code, setCode] = useState('')
  const codeRef = useRef<HTMLInputElement>(null)

  const headers: Record<string, string> = {
    ...(proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {}),
    'Content-Type': 'application/json',
  }

  useEffect(() => {
    if (!proxyUrl) return
    fetch(`${proxyUrl}/local/provider-auth/status`, { headers })
      .then(r => r.json() as Promise<{ configured: boolean }>)
      .then(data => {
        if (data.configured) {
          setState({ phase: 'verify', verifying: false, error: null })
        } else {
          loadSetupQr()
        }
      })
      .catch(() => setState({ phase: 'verify', verifying: false, error: null }))
  }, [proxyUrl])

  useEffect(() => {
    if (state.phase === 'verify' || state.phase === 'setup-confirm') {
      setTimeout(() => codeRef.current?.focus(), 50)
    }
  }, [state.phase])

  async function loadSetupQr() {
    if (!proxyUrl) return
    setState({ phase: 'checking' })
    try {
      const r = await fetch(`${proxyUrl}/local/provider-auth/setup`, { headers })
      const data = await r.json() as { secret: string; qr_base64: string; otpauth_uri: string }
      setState({ phase: 'setup-qr', secret: data.secret, qrBase64: data.qr_base64 })
    } catch {
      setState({ phase: 'verify', verifying: false, error: 'Error al cargar la configuración' })
    }
  }

  async function handleConfirmSetup() {
    if (state.phase !== 'setup-confirm') return
    if (code.length !== 6) return
    setState({ ...state, confirming: true, error: null })
    try {
      const r = await fetch(`${proxyUrl}/local/provider-auth/setup/confirm`, {
        method: 'POST', headers,
        body: JSON.stringify({ secret: state.secret, code }),
      })
      const data = await r.json() as { success?: boolean; error?: string }
      if (data.success) {
        setState({ phase: 'unlocked' })
      } else {
        setState({ ...state, confirming: false, error: data.error ?? 'Código incorrecto' })
        setCode('')
        codeRef.current?.focus()
      }
    } catch {
      setState({ ...state, confirming: false, error: 'Error de red' })
    }
  }

  async function handleVerify() {
    if (state.phase !== 'verify') return
    if (code.length !== 6) return
    setState({ ...state, verifying: true, error: null })
    try {
      const r = await fetch(`${proxyUrl}/local/provider-auth/verify`, {
        method: 'POST', headers,
        body: JSON.stringify({ code }),
      })
      const data = await r.json() as { valid?: boolean; error?: string }
      if (data.valid) {
        setState({ phase: 'unlocked' })
      } else {
        setState({ phase: 'verify', verifying: false, error: 'Código incorrecto. Intenta de nuevo.' })
        setCode('')
        codeRef.current?.focus()
      }
    } catch {
      setState({ phase: 'verify', verifying: false, error: 'Error de red' })
    }
  }

  if (state.phase === 'unlocked') {
    return <ProviderMode onBack={onBack} />
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0c1c2e 0%, #0b2d1f 45%, #0e1a32 100%)' }}
    >
      {/* Mesh orbs */}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none"
           style={{ background: 'radial-gradient(circle, hsl(160 84% 39% / 0.3) 0%, transparent 70%)' }} />
      <div className="absolute -bottom-40 -left-20 w-[400px] h-[400px] rounded-full blur-3xl pointer-events-none"
           style={{ background: 'radial-gradient(circle, #4f46e5 0%, transparent 70%)', opacity: 0.2 }} />

      <div className="relative w-full max-w-sm">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center backdrop-blur-sm">
              <span className="text-white font-bold text-xs">IS</span>
            </div>
            <div>
              <p className="font-semibold text-white text-sm leading-none">Modo Proveedor</p>
              <p className="text-white/40 text-xs mt-0.5">Satelab</p>
            </div>
          </div>
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/80 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-white/8"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Volver
          </button>
        </div>

        {/* ── Checking ── */}
        {state.phase === 'checking' && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-white/30" />
          </div>
        )}

        {/* ── Setup: QR ── */}
        {state.phase === 'setup-qr' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-6 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4.5 h-4.5 text-blue-400" style={{ width: '1.125rem', height: '1.125rem' }} />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base mb-1">Configura el acceso</h2>
                <p className="text-white/50 text-sm leading-relaxed">
                  Escanea con <span className="text-white/75 font-medium">Google Authenticator</span> u otra app TOTP para proteger este panel.
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="bg-white p-3.5 rounded-2xl shadow-xl">
                <img src={state.qrBase64} alt="QR TOTP" className="w-48 h-48 block" />
              </div>
            </div>

            <p className="text-white/30 text-xs text-center leading-relaxed">
              Después de escanearlo, ingresa el código de 6 dígitos para confirmar la activación.
            </p>

            <button
              onClick={() => setState({ phase: 'setup-confirm', secret: state.secret, qrBase64: state.qrBase64, confirming: false, error: null })}
              className="w-full py-3 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98]"
              style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
            >
              Ya lo escaneé → Ingresar código
            </button>
          </div>
        )}

        {/* ── Setup: confirm code ── */}
        {state.phase === 'setup-confirm' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-6 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4.5 h-4.5 text-blue-400" style={{ width: '1.125rem', height: '1.125rem' }} />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base mb-1">Confirmar configuración</h2>
                <p className="text-white/50 text-sm leading-relaxed">
                  Ingresa el código de 6 dígitos de tu autenticadora para activar la protección.
                </p>
              </div>
            </div>

            <OtpInput
              ref={codeRef}
              value={code}
              onChange={v => { setCode(v); if (state.phase === 'setup-confirm') setState({ ...state, error: null }) }}
              onSubmit={handleConfirmSetup}
              disabled={state.confirming}
              error={state.error}
            />

            <button
              onClick={handleConfirmSetup}
              disabled={code.length !== 6 || state.confirming}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
            >
              {state.confirming && <Loader2 className="w-4 h-4 animate-spin" />}
              Activar protección
            </button>

            <button
              onClick={() => { setCode(''); setState({ phase: 'setup-qr', secret: state.secret, qrBase64: state.qrBase64 }) }}
              className="text-center text-xs text-white/35 hover:text-white/70 transition-colors"
            >
              ← Ver QR de nuevo
            </button>
          </div>
        )}

        {/* ── Verify ── */}
        {state.phase === 'verify' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-6 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4.5 h-4.5 text-blue-400" style={{ width: '1.125rem', height: '1.125rem' }} />
              </div>
              <div>
                <h2 className="text-white font-semibold text-base mb-1">Acceso protegido</h2>
                <p className="text-white/50 text-sm leading-relaxed">
                  Ingresa el código de tu autenticadora para continuar.
                </p>
              </div>
            </div>

            <OtpInput
              ref={codeRef}
              value={code}
              onChange={v => { setCode(v); if (state.phase === 'verify') setState({ ...state, error: null }) }}
              onSubmit={handleVerify}
              disabled={state.verifying}
              error={state.error}
            />

            <button
              onClick={handleVerify}
              disabled={code.length !== 6 || state.verifying}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
            >
              {state.verifying && <Loader2 className="w-4 h-4 animate-spin" />}
              Verificar y entrar
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── OtpInput sub-component ────────────────────────────────────────────────────

import { forwardRef } from 'react'

interface OtpInputProps {
  value: string
  onChange: (v: string) => void
  onSubmit: () => void
  disabled: boolean
  error: string | null
}

const OtpInput = forwardRef<HTMLInputElement, OtpInputProps>(
  ({ value, onChange, onSubmit, disabled, error }, ref) => (
    <div className="flex flex-col gap-2">
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        value={value}
        onChange={e => onChange(e.target.value.replace(/\D/g, ''))}
        onKeyDown={e => e.key === 'Enter' && value.length === 6 && onSubmit()}
        disabled={disabled}
        placeholder="000 000"
        className={`w-full bg-white/5 border rounded-xl px-4 py-4 text-center text-2xl font-mono text-white tracking-[0.5em] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent transition-all disabled:opacity-40 placeholder:text-white/15 ${
          error ? 'border-red-500/60' : 'border-white/10'
        }`}
      />
      {error && (
        <div className="flex items-center justify-center gap-1.5 text-red-400 text-xs">
          <XCircle className="w-3.5 h-3.5 shrink-0" />
          {error}
        </div>
      )}
      {!error && value.length === 6 && (
        <div className="flex items-center justify-center gap-1.5 text-emerald-400 text-xs">
          <CheckCircle className="w-3.5 h-3.5" />
          Listo — presiona Enter o el botón
        </div>
      )}
    </div>
  )
)
OtpInput.displayName = 'OtpInput'
